import axios from 'axios';
import type { ApiDutiesResponse, ApiDuty, DutyAvailabilityResponse } from '@/rennovation/planning-view/types/api/duties-response';
import type { DeletedDutiesResponse } from '@/rennovation/planning-view/types/api/deleted-duty-response';
import type { CreateDutyPayload, DeleteDutyPayload, ExchangePayload } from '@/rennovation/planning-view/types/payloads/duty-payloads';
import { DateTime } from 'luxon';
import { AssignDutyApiPayload, AssignDutyApiResponse } from '@/rennovation/planning-view/types/api/assign-duty-api-response';
import { ApiCreateDutyRequest } from '@/rennovation/planning-view/types/api/duties-request';
import { DutyActivity } from '@/rennovation/planning-view/types/api/duties-response';
import { DutyBreak } from '@/rennovation/planning-view/types/api/duties-response';

export class DutyApiService {
	private baseUrl = '/api/duties';

	async findAllDuties(from: DateTime | undefined | string, to: DateTime | undefined | string): Promise<ApiDuty[]> {
		const response = await axios.post(`${this.baseUrl}/days`, {
			from,
			to,
		});
		return response.data;
	}

	async loadStatistics(date: string, users: string[]): Promise<{ data: any[] }> {
		const response = await axios.post<{ data: any[] }>('/api/statistics', {
			from: date,
			to: date,
			users: users,
		});
		return response.data;
	}

	async loadStatisticsByDayStatus(dayStatus: string): Promise<{ data: any[] }> {
		const response = await axios.get<{ data: any[] }>(`/api/statistics/dayStatus/${dayStatus}`);
		return response.data;
	}

	async loadExtraPayCurrency(): Promise<{ data: string }> {
		const response = await axios.get<{ data: string }>('/api/setup/extraPay_currency');
		return response.data;
	}

	async createDuty(payload: ApiCreateDutyRequest): Promise<ApiDuty> {
		const response = await axios.post<ApiDuty>(`${this.baseUrl}`, payload);
		return response.data;
	}

	async findDutyByID(dutyId: string): Promise<ApiDuty> {
		const response = await axios.get(`${this.baseUrl}/${dutyId}`, {
			params: {
				loadExchanges: true,
			},
		});
		return response.data;
	}

	async findDuties(from: DateTime | undefined | string, to: DateTime | undefined | string, users: string[]): Promise<ApiDuty[]> {
		const response = await axios.post(`${this.baseUrl}/roster`, {
			from,
			to,
			userList: users,
		});
		return response.data;
	}

	async fetchFilterResult(from: DateTime | undefined | string, to: DateTime | undefined | string, violations: any, userList: any): Promise<string[]> {
		const payload = { from, to, violations, userList };
		const cleanedPayload = Object.fromEntries(Object.entries(payload).filter(([_, value]) => value !== null && value !== undefined));
		const response = await axios.post(`${this.baseUrl}/roster/violation`, cleanedPayload);
		return response.data;
	}

	async loadUnallocatedDuties(days: string[]): Promise<ApiDuty[]> {
		const response = await axios.post(`${this.baseUrl}/unallocated`, days);
		return response.data;
	}

	async updateDuty(dutyID: string, changedDuty: AssignDutyApiPayload): Promise<AssignDutyApiResponse> {
		const result = await axios.patch(`${this.baseUrl}/${dutyID}`, changedDuty);
		return result.data;
	}

	async addToExchange(data: ExchangePayload): Promise<unknown> {
		const response = await axios.post('/api/exchange', data);
		return response.data;
	}

	async deleteDuty({ dutyId, changeComment }: DeleteDutyPayload): Promise<unknown> {
		const result = await axios.delete(`${this.baseUrl}/${dutyId}`, {
			data: {
				changeComment,
			},
		});
		return result.data;
	}

	async splitDuty(dutyId: string, splitBreak: DutyBreak, changeComment: string): Promise<ApiDuty> {
		const response = await axios.patch(`${this.baseUrl}/${dutyId}/split`, {
			splitBreak,
			changeComment,
		});

		return response.data;
	}

	async mergeDuty(dutyToMerge: string, targetDuty: string, changeComment: string): Promise<ApiDuty> {
		const response = await axios.patch(`${this.baseUrl}/${dutyToMerge}/merge`, {
			id: targetDuty,
			changeComment,
		});

		return response.data;
	}

	async loadDeletedDuties(chunk: number, from?: string | null, to?: string | null): Promise<DeletedDutiesResponse> {
		const params: Record<string, string> = {};
		if (from) params.from = from;
		if (to) params.to = to;
		const response = await axios.get<DeletedDutiesResponse>(`${this.baseUrl}/deleted/${chunk}`, { params });
		return response.data;
	}

	async restoreDuty(dutyId: string): Promise<void> {
		await axios.patch(`${this.baseUrl}/restore/${dutyId}`);
	}

	async fetchAvailableDutiesByDay(day: string): Promise<DutyAvailabilityResponse> {
		const result = await axios.get(`${this.baseUrl}/grouped?date=${day}`);
		return result.data;
	}

	async assignDuty(dutyId: string, payload: AssignDutyApiPayload): Promise<AssignDutyApiResponse> {
		const response = await axios.patch<AssignDutyApiResponse>(`${this.baseUrl}/${dutyId}`, payload);
		return response.data;
	}

	async loadDutyActivities(dutyId: string): Promise<DutyActivity[]> {
		const response = await axios.get(`${this.baseUrl}/${dutyId}/dutyactivities`);
		return response.data;
	}

	async findDutiesWithRootID(rootID: string, dutyID: string): Promise<ApiDuty[]> {
		const result = await axios.get(`${this.baseUrl}/root?rootId=${rootID}&dutyId=${dutyID}`);
		return result.data;
	}

	async updateRootDuties(duties) {
		try {
			const result = await axios.patch(`${this.baseUrl}`, duties);
			console.log(result);
			return result.data;
		} catch (err) {
			console.log(err);
			return err;
		}
	}
}
